import { AgGridReact } from "ag-grid-react";
import { useCallback, useRef } from "react";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { formatDate, validateString } from "src/utils/string/stringUtil";
import {
	restoreGridScrollState,
} from "src/utils/grid/gridStyleUtils";
import {
	ShowSuccessFetch,
	showError,
	showSuccess,
	showWarning,
} from "src/utils/toast/toastUtil";
import {
	getKSK1110_R01AllCount,
	getKSK1110_R01DetailList,
	getKSK1110_R01List,
	patchKSK1110_R01,
	postKSK1110_R01,
} from "./Api_KSK1110_R01";
import {
	defaultKSK1110_R01DetailDto,
	KSK1110_R01DetailDto,
	KSK1110_R01Dto,
	KSK1110_R01HeaderDto,
	KSK1110_R01_VALUE_FIELDS,
} from "./Dto_KSK1110_R01";
import { useKSK1110_R01Store } from "./Store_KSK1110_R01";
import {
	createKSK1110_R01SaveDetail,
	hasKSK1110_R01InspectionValue,
	normalizeKSK1110_R01InspectionDetail,
	validateKSK1110_R01InspectionValue,
} from "./Util_KSK1110_R01";

interface ContentKSK1110R01Props {
	gridRef: React.RefObject<AgGridReact<KSK1110_R01HeaderDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<KSK1110_R01DetailDto> | null>;
	setMessage: (message: string) => void;
}

const getKSK1110_R01DetailEditSignature = (detailList: KSK1110_R01DetailDto[]) =>
	JSON.stringify(detailList.map((detail) => [
		detail.valueIndex,
		...KSK1110_R01_VALUE_FIELDS.map((field) => String(detail[field] ?? "")),
		String(detail.remark ?? ""),
	]));

const hasKSK1110_R01PendingChanges = () => {
	const state = useKSK1110_R01Store.getState();
	const selected = state.selectedKSK1110_R01;
	if (!selected) return false;

	const originalInfo = state.originalKSK1110_R01DetailList[0]
		?? defaultKSK1110_R01DetailDto;
	const editInfo = state.editKSK1110_R01;
	const isInfoChanged =
		editInfo.inspectorEmp !== originalInfo.inspectorEmp
		|| editInfo.approvorEmpno !== originalInfo.approvorEmpno
		|| formatDate(editInfo.inspectDt) !== formatDate(originalInfo.inspectDt);
	const isDetailChanged =
		getKSK1110_R01DetailEditSignature(state.KSK1110_R01DetailList)
		!== getKSK1110_R01DetailEditSignature(state.originalKSK1110_R01DetailList);

	return isInfoChanged || isDetailChanged;
};

export const Content_KSK1110_R01 = ({
	gridRef,
	detailGridRef,
	setMessage,
}: ContentKSK1110R01Props) => {
	const {
		KSK1110_R01Filter,
		setKSK1110_R01PastFilter,
		setKSK1110_R01List,
		selectedKSK1110_R01,
		setSelectedKSK1110_R01,
		setEditKSK1110_R01,
		KSK1110_R01DetailList,
		setKSK1110_R01DetailList,
		setOriginalKSK1110_R01DetailList,
		setAllCount,
		scrollTop,
		setScrollTop,
		detailScrollTop,
		setDetailScrollTop,
		initData,
	} = useKSK1110_R01Store();

	const editKSK1110_R01 = useKSK1110_R01Store((state) => state.editKSK1110_R01);
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const detailRequestIdRef = useRef(0);

	const clearSelectionData = useCallback(() => {
		detailRequestIdRef.current += 1;
		setSelectedKSK1110_R01(undefined);
		setEditKSK1110_R01({ ...defaultKSK1110_R01DetailDto });
		setKSK1110_R01DetailList([]);
		setOriginalKSK1110_R01DetailList([]);
	}, [
		setEditKSK1110_R01,
		setKSK1110_R01DetailList,
		setOriginalKSK1110_R01DetailList,
		setSelectedKSK1110_R01,
	]);

	const fetchDetailData = useCallback(async (
		target: KSK1110_R01HeaderDto,
		preserveDetailScroll: boolean,
	) => {
		const requestId = detailRequestIdRef.current + 1;
		detailRequestIdRef.current = requestId;
		if (!preserveDetailScroll) setDetailScrollTop(null);
		setSelectedKSK1110_R01(target);

		const detailResponse = await getKSK1110_R01DetailList(
			target.transportNo,
			target.itemNo,
		);
		if (requestId !== detailRequestIdRef.current) return;
		const details = detailResponse.data.data.map(normalizeKSK1110_R01InspectionDetail);
		const info = details.length > 0
			? {
				...defaultKSK1110_R01DetailDto,
				...details[0],
				inspectType: details[0].inspectType || "IQC",
			}
			: { ...defaultKSK1110_R01DetailDto };

		setEditKSK1110_R01(info);
		setKSK1110_R01DetailList(details);
		setOriginalKSK1110_R01DetailList(details);

		if (details.length === 0) {
			showWarning("선택한 품목그룹에 등록된 수입검사 기준이 없습니다.");
		}

		if (preserveDetailScroll) {
			requestAnimationFrame(() => {
				if (detailGridRef.current?.api) {
					restoreGridScrollState(detailGridRef.current.api, detailScrollTop);
				}
			});
		} else {
			detailGridRef.current?.api.ensureIndexVisible(0, "top");
		}
	}, [
		detailGridRef,
		detailScrollTop,
		setDetailScrollTop,
		setEditKSK1110_R01,
		setKSK1110_R01DetailList,
		setOriginalKSK1110_R01DetailList,
		setSelectedKSK1110_R01,
	]);

	const executeDataFetch = useCallback(async (
		fetchType: "search" | "refetch",
	) => {
		const [listResponse, countResponse] = await Promise.all([
			getKSK1110_R01List(
				fetchType,
				KSK1110_R01Filter,
			),
			getKSK1110_R01AllCount(KSK1110_R01Filter),
		]);
		setKSK1110_R01List(listResponse.data.data);
		setAllCount(countResponse.data.data);
		setKSK1110_R01PastFilter({ ...KSK1110_R01Filter });

		return {
			list: useKSK1110_R01Store.getState().KSK1110_R01List,
			allCount: countResponse.data.data,
		};
	}, [
		KSK1110_R01Filter,
		setAllCount,
		setKSK1110_R01List,
		setKSK1110_R01PastFilter,
	]);

	const fetchData = useCallback(async () => {
		gridRef.current?.api.stopEditing();
		detailGridRef.current?.api.stopEditing();
		if (hasKSK1110_R01PendingChanges()) {
			showWarning("변경 내용을 저장하거나 초기화한 후 다시 조회해 주세요.");
			return;
		}
		setLoadingSpinner(true);
		try {
			clearSelectionData();
			setKSK1110_R01List([]);
			setScrollTop(null);
			setDetailScrollTop(null);

			const { list, allCount } = await executeDataFetch("search");
			if (list.length === 0) {
				setMessage(`조회 0건 / 전체 ${allCount.toLocaleString()}건`);
				showWarning("조회된 입고 검사 대상이 없습니다.");
				return;
			}
			await fetchDetailData(list[0], false);
			gridRef.current?.api.ensureIndexVisible(0, "top");
			ShowSuccessFetch(
				setMessage,
				`조회가 완료되었습니다. (총 ${list.length.toLocaleString()}/${allCount.toLocaleString()}건)`,
			);
		} catch {
			showError("입고 검사 대상 조회 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		clearSelectionData,
		detailGridRef,
		executeDataFetch,
		gridRef,
		fetchDetailData,
		setDetailScrollTop,
		setKSK1110_R01List,
		setLoadingSpinner,
		setMessage,
		setScrollTop,
	]);

	const fetchOne = useCallback(async (target: KSK1110_R01HeaderDto) => {
		detailGridRef.current?.api.stopEditing();
		const selected = useKSK1110_R01Store.getState().selectedKSK1110_R01;
		const isSameTarget = selected?.transportNo === target.transportNo
			&& selected.itemNo === target.itemNo;
		if (hasKSK1110_R01PendingChanges()) {
			showWarning(
				isSameTarget
					? "변경 내용을 저장하거나 초기화한 후 선택을 해제해 주세요."
					: "변경 내용을 저장하거나 초기화한 후 다른 입고 검사 대상을 선택해 주세요.",
			);
			return;
		}
		if (isSameTarget) {
			clearSelectionData();
			return;
		}
		setLoadingSpinner(true);
		try {
			await fetchDetailData(target, false);
		} catch {
			clearSelectionData();
			showError("검사 상세 조회 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [clearSelectionData, detailGridRef, fetchDetailData, setLoadingSpinner]);

	const buildSaveData = useCallback((): KSK1110_R01Dto | undefined => {
		if (!selectedKSK1110_R01) {
			showWarning("저장할 입고 검사 대상을 선택해 주세요.");
			return undefined;
		}
		if (!validateString(editKSK1110_R01.inspectorEmp)) {
			showWarning("검사자를 선택해 주세요.");
			return undefined;
		}
		if (KSK1110_R01DetailList.length === 0) {
			showWarning("저장할 검사 상세가 없습니다.");
			return undefined;
		}

		const normalizedDetails = KSK1110_R01DetailList.map(
			normalizeKSK1110_R01InspectionDetail,
		);
		if (!hasKSK1110_R01InspectionValue(normalizedDetails)) {
			showWarning("검사 결과를 한 건 이상 입력해 주세요.");
			return undefined;
		}
		const invalidDetail = normalizedDetails.find((detail) =>
			!validateKSK1110_R01InspectionValue(detail),
		);
		if (invalidDetail) {
			showWarning(`${invalidDetail.inspItemNm || invalidDetail.itemCd} 검사 결과 형식을 확인해 주세요.`);
			return undefined;
		}

		const info = {
			...editKSK1110_R01,
			inspectType: "IQC",
			inspectDt: editKSK1110_R01.inspectDt
				? formatDate(editKSK1110_R01.inspectDt)
				: undefined,
		};

		return {
			header: selectedKSK1110_R01,
			detailList: normalizedDetails.map((detail) =>
				createKSK1110_R01SaveDetail(detail, info)),
		};
	}, [KSK1110_R01DetailList, editKSK1110_R01, selectedKSK1110_R01]);

	const applySaveResponse = useCallback((savedData: KSK1110_R01Dto) => {
		const savedDetails = savedData.detailList.map(
			normalizeKSK1110_R01InspectionDetail,
		);
		const savedInfo = savedDetails.length > 0
			? {
				...defaultKSK1110_R01DetailDto,
				...savedDetails[0],
				inspectType: savedDetails[0].inspectType || "IQC",
			}
			: { ...defaultKSK1110_R01DetailDto };

		detailRequestIdRef.current += 1;
		setSelectedKSK1110_R01(savedData.header);
		setEditKSK1110_R01(savedInfo);
		setKSK1110_R01DetailList(savedDetails);
		setOriginalKSK1110_R01DetailList(savedDetails);
		requestAnimationFrame(() => {
			if (detailGridRef.current?.api) {
				restoreGridScrollState(detailGridRef.current.api, detailScrollTop);
			}
		});
	}, [
		detailGridRef,
		detailScrollTop,
		setEditKSK1110_R01,
		setKSK1110_R01DetailList,
		setOriginalKSK1110_R01DetailList,
		setSelectedKSK1110_R01,
	]);

	const refetchDataAfterSave = useCallback(async (
		transportNo: string,
		itemNo: string,
	) => {
		const { list } = await executeDataFetch("refetch");
		const refreshedHeader = list.find((header) =>
			header.transportNo === transportNo && header.itemNo === itemNo,
		);

		if (refreshedHeader) {
			setSelectedKSK1110_R01(refreshedHeader);
		} else {
			clearSelectionData();
			setDetailScrollTop(null);
		}
		requestAnimationFrame(() => {
			if (gridRef.current?.api) {
				restoreGridScrollState(gridRef.current.api, scrollTop);
			}
		});
	}, [
		clearSelectionData,
		executeDataFetch,
		gridRef,
		scrollTop,
		setDetailScrollTop,
		setSelectedKSK1110_R01,
	]);

	const handleSaveClick = useCallback(async () => {
		detailGridRef.current?.api.stopEditing();
		const payload = buildSaveData();
		if (!payload) return;

		const transportNo = payload.header.transportNo;
		const itemNo = payload.header.itemNo;

		setLoadingSpinner(true);
		try {
			const saveResponse =
				validateString(payload.detailList[0]?.inspectNo)
					? await patchKSK1110_R01(payload)
					: await postKSK1110_R01(payload);
			if (!saveResponse.data.successFlag) {
				showWarning("검사 결과 저장에 실패했습니다.");
				return;
			}

			const savedData = saveResponse.data.data;
			applySaveResponse(savedData);

			const successMessage = "검사 결과가 저장되었습니다.";
			showSuccess(successMessage);
			setMessage(successMessage);
			setTimeout(() => setMessage(""), 5000);

			try {
				await refetchDataAfterSave(transportNo, itemNo);
			} catch {
				showWarning("저장은 완료되었지만 대상 목록 재조회에 실패했습니다.");
			}
		} catch {
			showError("검사 결과 저장 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		applySaveResponse,
		buildSaveData,
		detailGridRef,
		setLoadingSpinner,
		setMessage,
		refetchDataAfterSave,
	]);

	const initContainerData = useCallback(() => {
		gridRef.current?.api.stopEditing();
		gridRef.current?.api.deselectAll();
		detailGridRef.current?.api.stopEditing();
		detailGridRef.current?.api.deselectAll();
		initData();
		setMessage("");
	}, [detailGridRef, gridRef, initData, setMessage]);

	return {
		fetchData,
		fetchOne,
		handleSaveClick,
		initContainerData,
	};
};
